import { COVERAGE_TYPE_CODESYSTEM, INSURANCE_COVERAGE_TYPE_CODE } from '@bariendo/lib/constants';
import { isInsuranceCoverage } from '@bariendo/lib/insurance-coverage';
import { readInsurancePrefill } from '@bariendo/lib/insurance-prefill';
import { PacInsuranceCardField } from '@bariendo/lib/patient-access-script';
import { createReference, getReferenceString, MedplumClient } from '@medplum/core';
import { Coverage, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { canSearchResourceType } from '@/services/permissions';
import { InsuranceCardDetails, insuranceCoverageInput, updateInsuranceCoverage } from './insuranceVerification';

/**
 * - `idle`: not read yet (a saved note, or the insurance beat not opened).
 * - `found`: the patient has an insurance Coverage; an edit saves to it.
 * - `none`: no insurance Coverage yet. The bot refuses to create one (only the
 *   switch to the Insurance track does), so an edit is held until the switch.
 * - `forbidden`: the role's policy has no Coverage search. The rows hide.
 * - `unreadable`: the read failed. Never taken for `none`: holding an edit
 *   would hide a value already on file.
 */
export type InsuranceCardStatus = 'idle' | 'loading' | 'found' | 'none' | 'forbidden' | 'unreadable';

export interface InsuranceCard {
  status: InsuranceCardStatus;
  /** What the Coverage holds. */
  saved: InsuranceCardDetails;
  /**
   * What was typed while there was no insurance Coverage, held for the switch
   * to the Insurance track to write. In memory only, never an answer, so never
   * on the note or its draft. It survives the card collapsing and the
   * navigator moving between patients (kept per note in this module), but not
   * a reload, a closed tab or signing out: the browser asks before a reload or
   * close while any note holds anything, signing out clears it, and saving the
   * note with anything held saves it to a Coverage that turned up or warns.
   */
  pending: InsuranceCardDetails;
  /** True while the held values are being written; the rows take no edit meanwhile. */
  writing: boolean;
  /**
   * Saves one field: straight to the Coverage (`saved`), or held for the
   * switch when there is none (`held`). An empty value clears the field.
   * Throws when the Coverage could not be read or the bot refused.
   */
  save: (field: PacInsuranceCardField, value: string) => Promise<'saved' | 'held'>;
  /**
   * Runs `write` with what is held, read at the moment of writing. Once it
   * lands, those values show as on file, are no longer held, and the
   * Coverage is read again. A failure keeps them held and rethrows.
   */
  writeHeld: (write: (held: InsuranceCardDetails) => Promise<void>) => Promise<void>;
  /**
   * For Save to chart, with what was held when Save was clicked: reads the
   * Coverage afresh and, if there is one now, saves the held values to it.
   * Holds nothing afterwards whatever the outcome; the card says which.
   */
  settleOnNoteSave: (held: InsuranceCardDetails) => Promise<NoteSaveOutcome>;
}

/** What became of held values when the note was saved. */
export type NoteSaveOutcome = 'nothing' | 'written' | 'none' | 'unreadable' | 'failed';

type CardRead = { status: 'found' | 'none' | 'forbidden' | 'unreadable'; saved: InsuranceCardDetails };

const NOTHING: InsuranceCardDetails = {};

/**
 * What each note's rows hold, so collapsing the card or leaving the patient
 * doesn't lose it. Keyed by the signed-in user as well as the note: a session
 * that ends without the menu's sign-out must not hand them to the next user.
 */
const HELD_BY_NOTE = new Map<string, InsuranceCardDetails>();

/** Asks before the page unloads while any note holds anything, mounted card or not. */
function askBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
  event.returnValue = '';
}

function setHeld(noteId: string, details: InsuranceCardDetails | undefined): void {
  if (details && hasAny(details)) HELD_BY_NOTE.set(noteId, details);
  else HELD_BY_NOTE.delete(noteId);
  if (HELD_BY_NOTE.size > 0) window.addEventListener('beforeunload', askBeforeUnload);
  else window.removeEventListener('beforeunload', askBeforeUnload);
}

/**
 * Drops everything held, for every note. Called on sign-out, which doesn't
 * reload the page: the next user in this tab must not inherit what was typed.
 */
export function clearHeldInsuranceCards(): void {
  HELD_BY_NOTE.clear();
  window.removeEventListener('beforeunload', askBeforeUnload);
}

function hasAny(details: InsuranceCardDetails): boolean {
  return Object.values(details).some(Boolean);
}

function detailsOf(coverage: Coverage): InsuranceCardDetails {
  const { memberId, groupNumber } = readInsurancePrefill(coverage, undefined);
  return { memberId, groupNumber };
}

/**
 * The patient's newest active insurance Coverage, searched as the
 * `update-insurance-coverage` bot searches it, so a reader shows the Coverage
 * an edit will land on. Callers check `canSearchResourceType` first.
 *
 * Never from the client's cache: the bot writes on the server, which does not
 * invalidate a cached search, so a read right after a save would show the
 * Coverage from before it.
 */
export async function findInsuranceCoverage(medplum: MedplumClient, patient: Patient): Promise<Coverage | undefined> {
  const coverages = await medplum.searchResources(
    'Coverage',
    {
      beneficiary: getReferenceString(patient),
      type: `${COVERAGE_TYPE_CODESYSTEM}|${INSURANCE_COVERAGE_TYPE_CODE}`,
      status: 'active',
      _sort: '-_lastUpdated',
    },
    { cache: 'no-cache' }
  );
  return coverages.find(isInsuranceCoverage);
}

/**
 * The patient's insurance card as the bot finds it: the newest active
 * insurance Coverage, searched the same way. A role whose policy has no
 * Coverage search gets `forbidden`, not a refused search.
 */
export async function readInsuranceCard(medplum: MedplumClient, patient: Patient): Promise<CardRead> {
  try {
    if (!canSearchResourceType(medplum, 'Coverage')) return { status: 'forbidden', saved: {} };
    const coverage = await findInsuranceCoverage(medplum, patient);
    return coverage ? { status: 'found', saved: detailsOf(coverage) } : { status: 'none', saved: {} };
  } catch {
    return { status: 'unreadable', saved: {} };
  }
}

/**
 * The PN card's member ID and group number rows. A row saves its field alone,
 * as the identity rows do: unlike the track switch's write, it does not carry
 * an insurer chosen on the call with it, so the field lands under whatever
 * payor the Coverage already names.
 *
 * The values live only on the patient's insurance Coverage, which Marketing,
 * Research Fellow and Quality Audit cannot read, while they can read the
 * note: so nothing here touches the answers. Saved through the `update-insurance-coverage` bot, as the
 * "Confirm their details" rows save to the Patient.
 *
 * - `active`: the note is a draft. A saved note (here or in another tab)
 *   holds nothing: what it held could no longer go anywhere, so it is dropped
 *   and the page stops asking before unload.
 * - `read`: read the Coverage now (the insurance beat is open).
 * - `rereadKey` (the patient's track): read again when it changes, so a switch
 *   made outside the card ends the holding.
 */
export function useInsuranceCard(
  patient: Patient | undefined,
  noteId: string | undefined,
  { active, read: enabled, rereadKey }: { active: boolean; read: boolean; rereadKey?: string }
): InsuranceCard {
  const medplum = useMedplum();
  const profile = medplum.getProfile();
  const heldKey = noteId && profile ? `${getReferenceString(profile)}|${noteId}` : undefined;
  const [read, setRead] = useState<CardRead | 'loading' | 'idle'>('idle');
  const [heldState, setPendingState] = useState<InsuranceCardDetails>(() =>
    heldKey ? (HELD_BY_NOTE.get(heldKey) ?? NOTHING) : NOTHING
  );
  const pending = active ? heldState : NOTHING;
  const [writing, setWriting] = useState(false);
  const patientId = patient?.id;
  // Read once per patient: the Patient object changes on every live update.
  const patientRef = useRef(patient);
  patientRef.current = patient;
  /** Bumped to read the Coverage again. */
  const [generation, setGeneration] = useState(0);
  /** Bumped by every landed save, so a read that started before one doesn't overwrite it. */
  const saveCount = useRef(0);

  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const setPending = useCallback(
    (next: (held: InsuranceCardDetails) => InsuranceCardDetails) => {
      const value = next(pendingRef.current);
      pendingRef.current = value;
      if (heldKey) setHeld(heldKey, value);
      setPendingState(value);
    },
    [heldKey]
  );

  useEffect(() => {
    const current = patientRef.current;
    if (!enabled || !current || !patientId) return undefined;
    let cancelled = false;
    const savesBefore = saveCount.current;
    setRead((latest) => (latest === 'idle' ? 'loading' : latest));
    void readInsuranceCard(medplum, current).then((result) => {
      if (!cancelled && saveCount.current === savesBefore) setRead(result);
    });
    return () => {
      cancelled = true;
    };
  }, [medplum, enabled, patientId, generation, rereadKey]);

  // A note saved meanwhile holds nothing any more.
  useEffect(() => {
    if (!active && heldKey) setHeld(heldKey, undefined);
  }, [active, heldKey]);

  const readRef = useRef(read);
  readRef.current = read;

  const save = useCallback(
    async (field: PacInsuranceCardField, value: string): Promise<'saved' | 'held'> => {
      const current = readRef.current;
      const cleaned = value.trim() || undefined;
      const target = patientRef.current;
      if (typeof current === 'string' || (current.status !== 'found' && current.status !== 'none') || !target) {
        throw new Error('The insurance card could not be read');
      }
      if (current.status === 'none') {
        setPending((held) => {
          const { [field]: _dropped, ...rest } = held;
          return cleaned ? { ...rest, [field]: cleaned } : rest;
        });
        return 'held';
      }
      const saved = await updateInsuranceCoverage(medplum, {
        patient: createReference(target),
        [field]: cleaned ?? null,
      });
      saveCount.current += 1;
      // Only this field: a slower response to another row's save must not undo it.
      const stored = saved?.resourceType === 'Coverage' ? detailsOf(saved)[field] : cleaned;
      setRead((latest) =>
        typeof latest === 'string' ? latest : { ...latest, saved: { ...latest.saved, [field]: stored } }
      );
      // A value held before the Coverage turned up (a switch made outside the
      // card) is settled by this save.
      setPending((held) => {
        const { [field]: _settled, ...rest } = held;
        return rest;
      });
      return 'saved';
    },
    [medplum, setPending]
  );

  const writeHeld = useCallback(
    async (write: (held: InsuranceCardDetails) => Promise<void>) => {
      const held = pendingRef.current;
      setWriting(true);
      try {
        await write(held);
      } finally {
        setWriting(false);
      }
      // A read started before the write (the switch changes the track) must not land over it.
      saveCount.current += 1;
      // Taken as found at once, not after the re-read: the switch has created
      // or adopted the Coverage, and an edit made meanwhile must go to it
      // rather than be held for a switch that has already happened.
      setRead((latest) => ({
        status: 'found',
        saved: { ...(typeof latest === 'string' ? {} : latest.saved), ...held },
      }));
      setPending((current) => {
        const kept = { ...current };
        for (const field of Object.keys(held) as PacInsuranceCardField[]) {
          if (kept[field] === held[field]) delete kept[field];
        }
        return kept;
      });
      setGeneration((count) => count + 1);
    },
    [setPending]
  );

  const settleOnNoteSave = useCallback(
    async (held: InsuranceCardDetails): Promise<NoteSaveOutcome> => {
      const target = patientRef.current;
      if (!hasAny(held)) return 'nothing';
      try {
        // Afresh: the status on screen may be from before a switch in another
        // tab, or never read if the insurance beat wasn't reopened.
        const fresh = target ? await readInsuranceCard(medplum, target) : undefined;
        if (!target || fresh?.status !== 'found') return fresh?.status === 'none' ? 'none' : 'unreadable';
        const input = insuranceCoverageInput(target, { values: {} }, held);
        if (input) await updateInsuranceCoverage(medplum, input);
        return 'written';
      } catch {
        return 'failed';
      } finally {
        setPending(() => NOTHING);
      }
    },
    [medplum, setPending]
  );

  return useMemo(
    () => ({
      status: typeof read === 'string' ? read : read.status,
      saved: typeof read === 'string' ? NOTHING : read.saved,
      pending,
      writing,
      save,
      writeHeld,
      settleOnNoteSave,
    }),
    [read, pending, writing, save, writeHeld, settleOnNoteSave]
  );
}
